<template>
  <div class="activity-stage whack-a-mole">
    <div class="word" v-if="getWordFromRounds">
      <blinking-cursor-text v-for="word in [getWordFromRounds]" :round-id="currentRound.roundIndex" :key="word.answer"
        :text-step="word.stepIndex" :text="word.answer"></blinking-cursor-text>
    </div>
    <div class="sentence" v-if="getSentenceFromRounds">
      <blinking-cursor-text v-for="sentence in [getSentenceFromRounds]" :round-id="currentRound.roundIndex"
        :key="sentence.answer" :text-step="sentence.stepIndex"
        :text="sentence.answer ? sentence.answer : ''"></blinking-cursor-text>
    </div>
    <div class="moles-stage">
      <figure><img class="sky-image" :src="sky" /></figure>
      <text-display-list class="syllables-wrapper" :text-size="'large'">
        <li v-for="stepSyll in stepsSyllables" :key="stepSyll.stepIndex" class="syllable-container"
          :class="`syllable-${stepSyll.order} syllable-container-${getNestsNumber}`">
          <div :class="`dynamic-mole dynamic-mole-${stepSyll.stepIndex}`">
            <div class="syllable-banner">
              <blinking-cursor-text class="type-line" v-for="item in [stepSyll]" :round-id="currentRound.roundIndex"
                :key="item.answer" :text-step="item.stepIndex" :text="item.answer">
              </blinking-cursor-text>
            </div>
            <img class="mole-image" :src="stepSyll.stepIndex % 2 == 0 ? leftMole : rightMole" />
          </div>
          <figure v-if="stepSyll.stepIndex < getNestsNumber">
            <img class="nest-image" :src="nest" />
          </figure>
        </li>
      </text-display-list>
      <figure><img class="grass-image" :src="grass" /></figure>
      <div class="green-land"></div>
    </div>
    <slot name="keyboard"></slot>
  </div>
</template>

<script>
import { TweenMax, Elastic, TimelineLite } from 'gsap';

import ActivityManager from 'mixins/ActivityManager';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ErrorLogger from 'mixins/ErrorLogger';

import BlinkingCursorText from 'components/text-displays/BlinkingCursorText.vue';
import TextDisplayList from 'components/text-displays/TextDisplayList.vue';

import grass from 'assets/images/activities/whack-a-mole/grass.png';
import nest from 'assets/images/activities/whack-a-mole/nest.png';
import leftMole from 'assets/images/activities/whack-a-mole/left-mole.png';
import rightMole from 'assets/images/activities/whack-a-mole/right-mole.png';
import sky from 'assets/images/activities/whack-a-mole/sky.jpg';

export default {
  mixins: [ActivityManager, TypingActivityHelper, ErrorLogger],
  components: {
    BlinkingCursorText,
    TextDisplayList
  },
  data() {
    return {
      grass,
      nest,
      leftMole,
      rightMole,
      sky
    };
  },
  beforeDestroy() {
    TweenMax.killAll();
  },
  computed: {
    stepsSyllables() {
      if (!this.currentRound.steps) {
        return [];
      }
      return this.currentRound.steps.filter(
        step => step.stepIndex < this.currentRound.steps.length - 2
      );
    },
    getNestsNumber() {
      return (
        this.currentRound.steps &&
        this.currentRound.steps.length - 2 - this.currentRound.randomizeCount
      );
    },
    getMoleIndex() {
      return this.currentRound.steps.find(
        step =>
          step.answer === this.currentStep.answer && step.stepIndex === this.currentStep.stepIndex
      );
    },
    getWordFromRounds() {
      return this.currentRound.steps && this.currentRound.steps[this.currentRound.steps.length - 2];
    },
    getSentenceFromRounds() {
      return this.currentRound.steps && this.currentRound.steps[this.currentRound.steps.length - 1];
    }
  },
  methods: {
    async loadActivity() {
      this.createRounds(this.getActivityState());
      await this.loadSounds();
    },

    async loadSounds() {
      this.sfx = await this.getActivityRoundSounds(
        this.rounds,
        this.spokenWordsPrefix,
        'ft',
        'word'
      );
    },

    getActivityState() {
      const { rounds } = this.activityData;

      return {
        rounds,
        instructions: {
          written: 'Type the syllables. Then type the word.',
          voice: `${this.voiceInstructionsPrefix}/syllablepop-up-${this.getRandomVoice()}-1.mp3`,
          delay: 6
        }
      };
    },

    startActivity() {
      const timeline = new TimelineLite();

      this.setupNewRound();
      this.errorLoggerStart();
      timeline
        // .delay(3)
        .to('.moles-stage,.sky-image,.nest-image,.grass-image,.dynamic-mole', 0.5, { autoAlpha: 1 })
        // .delay(1)
        .call(this.checkMole)
        .delay(0.5)
        .call(this.enableKeyboard)
        .call(this.showKeyboard);
    },

    handleCorrectKey() {
      this.sndType();
      this.incrementTypingIndex();
      if (this.isEndOfCurrentText) {
        this.disableKeyboard();
        this.finishStep();
      }
      if (this.isEndOfRound) {
        this.handleRoundEnd();
      }
    },

    finishStep() {
      this.sndBell();
      this.finishStepType(this.currentStep.stepType);
      this.completeStep();
      if (this.currentStep.stepType === 'word' && !this.getSentenceFromRounds.answer) {
        this.handleRoundEnd();
        return;
      }

      this.setCurrentStep();
      if (this.getMoleIndex) {
        if (this.currentStep.stepType === 'USyll') {
          this.disableKeyboard();
        }
        const timeline = new TimelineLite();
        timeline.delay(1).call(this.checkMole);
      }
    },

    showMole(show, moleIndex) {
      if (show) {
        TweenMax.to(`.dynamic-mole-${moleIndex}`, 0.5, {
          css: { y: -280 },
          onComplete: this.enableKeyboard
        });
      } else {
        TweenMax.to(`.dynamic-mole-${moleIndex}`, 0.5, {
          ease: Elastic.easeIn.config(1, 0.75),
          y: 0,
          onComplete: this.disableKeyboard
        });
      }
    },

    hideAllMoles() {
      TweenMax.to('.dynamic-mole', 0.5, {
        ease: Elastic.easeIn.config(1, 0.75),
        y: 0
      });
    },

    finishStepType(stepType) {
      const timeline = new TimelineLite();
      if (stepType === 'USyll') {
        this.showMole(false, this.currentStep.stepIndex);
      }
      if (stepType === 'word') {
        this.playStepSound(this.sfx);
        timeline
          .call(this.hideAllMoles)
          .delay(1)
          .to('.sentence', 0.5, { autoAlpha: 1 }, 1.5);
      }
    },

    handleRoundEnd() {
      const timeline = new TimelineLite();

      if (this.isEndOfActivity || this.isLastRound) {
        timeline
          .delay(1)
          .to(
            '.moles-stage,.sky-image,.nest-image,.grass-image,.dynamic-mole,.sentence,.word',
            0.5,
            { opacity: 0 }
          )
          .call(this.completeActivity);
      } else {
        timeline
          .to(
            '.moles-stage,.sky-image,.nest-image,.grass-image,.dynamic-mole,.sentence,.word',
            0.5,
            { opacity: 0 }
          )
          .call(this.setupNewRound)
          .delay(2)
          .to('.moles-stage,.sky-image,.nest-image,.grass-image,.dynamic-mole,.word', 0.5, {
            opacity: 1
          })
          .call(this.checkMole);
      }
    },

    checkMole() {
      this.$nextTick(() => {
        this.showMole(true, this.getMoleIndex.stepIndex);
      });
    },

    getKeyboardConfig() {
      return {
        showKeyboardAtStart: false
      };
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../assets/sass/_vars';

.whack-a-mole {
  margin-top: -30px;

  figure,
  ul,
  li {
    margin: 0;
    padding: 0;
  }

  .word {
    margin: 30px auto;
    font-size: 40px;
    letter-spacing: 2px;
    display: flex;
    justify-content: center;
  }

  .sentence {
    visibility: hidden;
    width: 80%;
    font-size: 30px;
    letter-spacing: 2px;
    margin: auto;
  }

  .green-land {
    position: absolute;
    bottom: 0;
    width: 100%;
    height: 260px;
    background-color: $grassGreen;
  }

  .moles-stage {
    visibility: hidden;
    background-color: $grassGreen;

    .sky-image {
      height: 460px;
      position: absolute;
      top: 0;
      z-index: -1;
    }

    .grass-image {
      z-index: 2;
      position: absolute;
      left: 0;
      top: 425px;
      width: 100%;
      height: 60px;
    }

    .syllables-wrapper {
      display: block;
      position: absolute;
      top: 130px;
      left: 0;
      width: 100%;
      text-align: center;

      .syllable-container {
        display: inline-block;
        text-align: center;
        margin: auto;
        position: absolute;
        height: 350px;
        overflow: hidden;
        width: 100%;

        .dynamic-mole {
          position: absolute;
          top: 320px;
          width: 100%;

          .syllable-banner {
            background-color: $kwtWhite;
            border: 1px solid $kwtBlack;
            border-radius: 5px;
            width: 180px;
            height: 110px;
            text-align: center;
            margin: auto;

            .type-line {
              margin: 25px auto;
            }
          }

          .mole-image {
            display: block;
            margin: -15px auto;
          }
        }

        .nest-image {
          width: 100%;
          height: 60px;
          top: 295px;
          position: absolute;
          z-index: 2;
          left: 0;
        }
      }

      .syllable-container-2 {
        width: 30%;

        &.syllable-0 {
          left: 20%;
        }

        &.syllable-1 {
          left: 50%;
        }
      }

      .syllable-container-3 {
        width: 30%;

        &.syllable-0 {
          left: 5%;
        }

        &.syllable-1 {
          left: 35%;
        }

        &.syllable-2 {
          left: 65%;
        }
      }

      .syllable-container-4 {
        width: 25%;

        &.syllable-0 {
          left: 0;
        }

        &.syllable-1 {
          left: 25%;
        }

        &.syllable-2 {
          left: 50%;
        }

        &.syllable-3 {
          left: 75%;
        }

        .syllable-banner {
          width: 95%;
        }
      }

      .syllable-container-5 {
        width: 20%;

        &.syllable-0 {
          left: 0;
        }

        &.syllable-1 {
          left: 20%;
        }

        &.syllable-2 {
          left: 40%;
        }

        &.syllable-3 {
          left: 60%;
        }

        &.syllable-4 {
          left: 80%;
        }

        .syllable-banner {
          width: 90%;
        }
      }
    }
  }
}

@media screen and (max-height: 800px) {
  .word {
    position: absolute;
    top: 30px;
    width: 100%;
    z-index: 2;
  }

  .sentence {
    position: absolute;
    top: 110px;
    z-index: 2;
    width: 100%;

    p {
      width: 80%;
      margin: auto;
    }
  }

  .sky-image {
    top: 30px !important;
    z-index: 0 !important;
    height: 500px;
  }
}
</style>
